import { Navigate, useRoutes } from "react-router-dom";
import { ROUTE_PATHS } from "./index";
import { extractEventDetailPath, extractHiderHeaderPaths, extractStringFromUrl } from "../utils/commonUtils";
import useUserInfo from "../hooks/reduxHelpersHooks/useUserInfo";
import DashboardLayout from "../layouts/dashboard/DashboardLayout";
import AuthLayout from "../layouts/AuthLayout";
import LoginPage from "../pages/Auth/LoginPage";
import ForgotPasswordPage from "../pages/Auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/Auth/ResetPasswordPage";
import ChangePasswordPage from "../pages/Auth/ChangePasswordPage";
import { useUserContext } from "../context/UserContext";
import Approved from "../pages/Events/Active/Approved";
import DirectRequestEvents from "../pages/Events/Active/DirectRequest";
import AIDirectRequest from "../pages/Events/Active/AIDirectRequest";
import InProgressEvents from "../pages/Events/Active/InProgress";
import EventDetail from "../pages/Events/EventDetail";
import EventHistoryCompleted from "../pages/Events/History/Completed";
import EventHistoryDeclined from "../pages/Events/History/Declined";
import Notification from "../pages/Notification";
import ChatsListPage from "../pages/Chats/ChatsListPage";
import ChatDetailPage from "../pages/Chats/ChatDetailPage";
import SupportChat from "../pages/SupportChat/SupportChat";
import PrePaidRequests from "../pages/PrePaidRequests/index";
import QuoteForm from "../pages/QuoteForm/QuoteForm";
import NotFoundPage from "../pages/NotFoundPage";

// B2B
import ActiveEventsRFPS from "../pages/B2B/CorporateEvents/ActiveEventsRFPS/ActiveEventsRFPS";
import ApprovedEvents from "../pages/B2B/CorporateEvents/ApprovedEvents/ApprovedEvents";
import DeclinedEvents from "../pages/B2B/CorporateEvents/DeclinedEvents/DeclinedEvents";
import CompletedEvents from "../pages/B2B/CorporateEvents/CompletedEvents/CompletedEvents";
import CorporateEventDetails from "../pages/B2B/CorporateEventDetails/CorporateEventDetails";

export const Router = () => {
	const { isLoggedIn, mustChangePassword } = useUserInfo();
	const { logout } = useUserContext();
	const pathname = window.location.pathname || "";
	const getInnerPageString = extractStringFromUrl(pathname);
	const getInnerPagePath = extractEventDetailPath(getInnerPageString, "event-detail");
	const getB2BEventDetailPath = pathname.includes("b2b/events");
	const hideHeader = extractHiderHeaderPaths(pathname);

	const routes = useRoutes([
		{
			path: ROUTE_PATHS.LOGIN,
			element: isLoggedIn ? (
				<Navigate to={mustChangePassword ? ROUTE_PATHS.CHANGE_PASSWORD : ROUTE_PATHS.DASHBOARD} />
			) : (
				<LoginPage />
			),
		},
		{
			path: ROUTE_PATHS.FORGOT_PASSWORD,
			element: <AuthLayout />,
			children: [{ index: true, element: <ForgotPasswordPage /> }],
		},
		{
			path: ROUTE_PATHS.RESET_PASSWORD,
			element: <AuthLayout />,
			children: [{ index: true, element: <ResetPasswordPage /> }],
		},
		{
			// Only for a vendor still on the temporary password from the
			// Published email. Closing it logs out — there is no way past it.
			path: ROUTE_PATHS.CHANGE_PASSWORD,
			element: !isLoggedIn ? (
				<Navigate to={ROUTE_PATHS.LOGIN} />
			) : !mustChangePassword ? (
				<Navigate to={ROUTE_PATHS.DASHBOARD} />
			) : (
				<AuthLayout onClose={() => logout(ROUTE_PATHS.LOGIN)} />
			),
			children: [{ index: true, element: <ChangePasswordPage /> }],
		},
		{
			path: ROUTE_PATHS.DASHBOARD,
			element:
				// Authentication only. Whether the vendor has accepted the current
				// T&Cs is authorization, handled by the gate inside DashboardLayout
				// — it needs the layout mounted to render the blocking modal.
				// A temporary password is replaced before any of that.
				!isLoggedIn ? (
					<Navigate to={ROUTE_PATHS.LOGIN} />
				) : mustChangePassword ? (
					<Navigate to={ROUTE_PATHS.CHANGE_PASSWORD} />
				) : (
					<DashboardLayout
						hideHeader={hideHeader}
						getInnerPagePath={getInnerPagePath}
						getB2BEventDetailPath={getB2BEventDetailPath}
					/>
				),
			children: [
				{ element: <AIDirectRequest />, index: true },
				{
					path: ROUTE_PATHS.ACTIVE_EVENTS,
					element: <DirectRequestEvents />,
				},
				{
					path: ROUTE_PATHS.IN_PROGRESS_EVENTS,
					element: <InProgressEvents />,
				},
				{
					path: ROUTE_PATHS.DIRECTORY_REQUEST_EVENTS,
					element: <DirectRequestEvents />,
				},
				{
					path: ROUTE_PATHS.AI_DIRECT_REQUEST,
					element: <AIDirectRequest />,
				},
				{ path: ROUTE_PATHS.APPROVED_EVENTS, element: <Approved /> },
				{
					path: ROUTE_PATHS.EVENT_HISTORY_DECLINED,
					element: <EventHistoryDeclined />,
				},
				{
					path: ROUTE_PATHS.EVENT_HISTORY_COMPLETED,
					element: <EventHistoryCompleted />,
				},
				{
					path: ROUTE_PATHS.EVENT_DETAIL,
					element: <EventDetail getInnerPagePath={getInnerPagePath} />,
				},
				{
					path: ROUTE_PATHS.NOTIFICATION,
					element: <Notification />,
				},
				{
					path: ROUTE_PATHS.CHATS,
					element: <ChatsListPage />,
					children: [
						{
							path: ROUTE_PATHS.CHAT_DETAIL,
							element: <ChatDetailPage />,
						},
						{
							path: ROUTE_PATHS.ADMIN_VENDOR_CHAT,
							element: <SupportChat />,
						},
					],
				},
				{
					path: ROUTE_PATHS.QUOTE_FORM,
					element: <QuoteForm />,
				},
				{
					path: ROUTE_PATHS.PRE_PAID_REQUESTS,
					element: <PrePaidRequests />,
				},

				/* --------------------------------- B2B --------------------------------- */

				{ path: ROUTE_PATHS.B2B.ACTIVE_EVENTS, element: <ActiveEventsRFPS /> },
				{ path: ROUTE_PATHS.B2B.APPROVED_EVENTS, element: <ApprovedEvents /> },
				{ path: ROUTE_PATHS.B2B.DECLINED_EVENTS, element: <DeclinedEvents /> },
				{ path: ROUTE_PATHS.B2B.COMPLETED_EVENTS, element: <CompletedEvents /> },
				{ path: ROUTE_PATHS.B2B.EVENT_DETAIL, element: <CorporateEventDetails /> },
			],
		},
		{
			path: "*",
			element: <AuthLayout />,
			children: [{ path: "*", element: <NotFoundPage /> }],
		},
	]);
	return routes;
};
